import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Type, Hash, CheckSquare, Calendar, ChevronDown, Tags, Link2, Plus, Trash2, GripVertical,
  Globe, FolderOpen, Database,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { customFieldsApi } from '../services/api';
import { useDocumentsQuery } from '../hooks/queries';
import { toast } from 'sonner';
import { APP_COLOR_VALUES as OPTION_COLORS } from '../constants/colors';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
} from './ui/responsive-modal';

const FIELD_TYPE_ICONS = {
  text: Type,
  number: Hash,
  checkbox: CheckSquare,
  date: Calendar,
  select: ChevronDown,
  multi_select: Tags,
  url: Link2,
  relation: Database,
};

const FIELD_TYPE_KEYS = ['text', 'number', 'checkbox', 'date', 'select', 'multi_select', 'url', 'relation'];

const AddFieldModal = ({ onClose, onFieldCreated, onFieldUpdated, initialDef = null, projectId = null }) => {
  const { t } = useTranslation();
  const isEditMode = !!initialDef;

  const [selectedType, setSelectedType] = useState(isEditMode ? initialDef.field_type : 'text');
  const [fieldName, setFieldName] = useState(isEditMode ? initialDef.name : '');
  const [scope, setScope] = useState(
    isEditMode ? (initialDef.scope || 'global') : (projectId ? 'project' : 'global')
  );
  const [options, setOptions] = useState(
    isEditMode && initialDef.options?.length > 0
      ? initialDef.options
      : [
          { id: crypto.randomUUID(), label: t('ui_views.add_field.option_n', { n: 1 }), color: '#6B7280' },
          { id: crypto.randomUUID(), label: t('ui_views.add_field.option_n', { n: 2 }), color: '#3B82F6' },
        ]
  );
  const [targetDatabaseId, setTargetDatabaseId] = useState(
    isEditMode ? (initialDef.target_database_id || '') : ''
  );
  const [relationCardinality, setRelationCardinality] = useState(
    isEditMode ? (initialDef.relation_cardinality || 'many') : 'many'
  );
  // List of databases the user can target. Loaded only when relation type is
  // selected; we filter by type=database so we don't ship every Document.
  const databasesQuery = useDocumentsQuery(
    { type: 'database' },
    { enabled: selectedType === 'relation' }
  );
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef(null);
  const draggedId = useRef(null);

  const handleDragStart = (id) => {
    draggedId.current = id;
  };

  const handleDragOver = (e, overId) => {
    e.preventDefault();
    if (draggedId.current === overId) return;
    setOptions(prev => {
      const from = prev.findIndex(o => o.id === draggedId.current);
      const to = prev.findIndex(o => o.id === overId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
  };

  useEffect(() => {
    if (isEditMode && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode && selectedType && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditMode, selectedType]);

  const needsOptions = selectedType === 'select' || selectedType === 'multi_select';
  const isRelation = selectedType === 'relation';

  const addOption = () => {
    setOptions(prev => [
      ...prev,
      { id: crypto.randomUUID(), label: t('ui_views.add_field.option_n', { n: prev.length + 1 }), color: OPTION_COLORS[prev.length % OPTION_COLORS.length] }
    ]);
  };

  const updateOption = (id, field, value) => {
    setOptions(prev => prev.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  const removeOption = (id) => {
    setOptions(prev => prev.filter(o => o.id !== id));
  };

  const handleSave = async () => {
    if (!isEditMode && !selectedType) return;
    const name = fieldName.trim();
    if (!name) {
      toast.error(t('tasks.enter_field_name'));
      nameInputRef.current?.focus();
      return;
    }
    if (needsOptions && options.length === 0) {
      toast.error(t('tasks.add_at_least_one_option'));
      return;
    }
    if (isRelation && !targetDatabaseId) {
      toast.error(t('tasks.relation_target_required', { defaultValue: 'Pick a target database.' }));
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        const payload = {
          name,
          options: needsOptions ? options.map(o => ({ id: o.id, label: o.label, color: o.color })) : [],
        };
        if (isRelation) {
          payload.target_database_id = targetDatabaseId;
          payload.relation_cardinality = relationCardinality;
        }
        const res = await customFieldsApi.update(initialDef.id, payload);
        toast.success(t('tasks.field_updated', { name }));
        onFieldUpdated(res.data);
      } else {
        const payload = {
          name,
          field_type: selectedType,
          options: needsOptions ? options.map(o => ({ id: o.id, label: o.label, color: o.color })) : [],
          scope,
          project_id: scope === 'project' ? projectId : null,
        };
        if (isRelation) {
          payload.target_database_id = targetDatabaseId;
          payload.relation_cardinality = relationCardinality;
        }
        const res = await customFieldsApi.create(payload);
        toast.success(t('tasks.field_created', { name }));
        onFieldCreated(res.data);
      }
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || (isEditMode ? t('tasks.failed_to_update_field') : t('tasks.failed_to_create_field'));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const typeInfo = selectedType
    ? { icon: FIELD_TYPE_ICONS[selectedType], label: t(`tasks.field_types.${selectedType}`) }
    : null;

  return (
    <ResponsiveModal open onOpenChange={(open) => { if (!open) onClose(); }}>
      <ResponsiveModalContent className="bg-popover border-border md:rounded-2xl">
        <ResponsiveModalHeader className="border-border">
          <ResponsiveModalTitle className="text-foreground font-semibold text-base">
            {isEditMode ? t('tasks.edit_field') : (selectedType ? t('tasks.configure_field') : t('tasks.add_field'))}
          </ResponsiveModalTitle>
          <ResponsiveModalClose className="text-muted-foreground hover:bg-transparent hover:text-foreground p-0" />
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="p-5 space-y-5">
          {/* Field type - read-only badge in edit mode, selector in create mode */}
          {isEditMode ? (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{t('tasks.field_type')}</p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/60 border border-border w-fit">
                {typeInfo && <typeInfo.icon className="w-4 h-4 text-muted-foreground" />}
                <span className="text-sm text-foreground/80">{typeInfo?.label ?? selectedType}</span>
                <span className="text-xs text-muted-foreground/70 ml-1">({t('tasks.cannot_change')})</span>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">{t('tasks.field_type')}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {FIELD_TYPE_KEYS.map((type) => {
                  const Icon = FIELD_TYPE_ICONS[type];
                  const label = t(`tasks.field_types.${type}`);
                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedType(type)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all ${
                        selectedType === type
                          ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                          : 'border-border bg-muted/50 text-muted-foreground hover:border-input hover:text-foreground/80'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs font-medium leading-none">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scope selector - only in create mode when inside a project */}
          {!isEditMode && projectId && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{t('tasks.availability')}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setScope('global')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                    scope === 'global'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'border-border text-muted-foreground hover:border-input hover:text-foreground/80'
                  }`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  {t('tasks.all_projects')}
                </button>
                <button
                  onClick={() => setScope('project')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                    scope === 'project'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'border-border text-muted-foreground hover:border-input hover:text-foreground/80'
                  }`}
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  {t('tasks.this_project_only')}
                </button>
              </div>
            </div>
          )}

          {/* Scope read-only badge in edit mode */}
          {isEditMode && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{t('tasks.availability')}</p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/60 border border-border w-fit">
                {scope === 'project'
                  ? <FolderOpen className="w-4 h-4 text-muted-foreground" />
                  : <Globe className="w-4 h-4 text-muted-foreground" />}
                <span className="text-sm text-foreground/80">
                  {scope === 'project' ? t('tasks.this_project_only') : t('tasks.all_projects')}
                </span>
                <span className="text-xs text-muted-foreground/70 ml-1">({t('tasks.cannot_change')})</span>
              </div>
            </div>
          )}

          {/* Field name */}
          {(isEditMode || selectedType) && (
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">{t('tasks.field_name')}</label>
              <Input
                ref={nameInputRef}
                value={fieldName}
                onChange={e => setFieldName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                placeholder={t('ui_views.add_field.name_placeholder', { example: typeInfo?.label ?? t('tasks.field_name') })}
                className="bg-accent/60 border-border text-foreground placeholder:text-muted-foreground/70"
              />
            </div>
          )}

          {/* Relation target picker */}
          {isRelation && (
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                {t('tasks.relation_target', { defaultValue: 'Target database' })}
              </label>
              <select
                value={targetDatabaseId}
                onChange={e => setTargetDatabaseId(e.target.value)}
                className="w-full bg-accent/60 border border-border text-foreground rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500/50"
              >
                <option value="">— {t('common.select', { defaultValue: 'Select' })} —</option>
                {(databasesQuery.data || []).map(d => (
                  <option key={d.id} value={d.id}>{d.title || t('documents.untitled')}</option>
                ))}
              </select>
              {(databasesQuery.data || []).length === 0 && !databasesQuery.isPending && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {t('tasks.no_databases_yet', { defaultValue: 'Create a Database first to link to it.' })}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setRelationCardinality('many')}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${
                    relationCardinality === 'many'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'border-border text-muted-foreground hover:border-input'
                  }`}
                >
                  {t('tasks.relation_many', { defaultValue: 'Many' })}
                </button>
                <button
                  type="button"
                  onClick={() => setRelationCardinality('one')}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${
                    relationCardinality === 'one'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'border-border text-muted-foreground hover:border-input'
                  }`}
                >
                  {t('tasks.relation_one', { defaultValue: 'One' })}
                </button>
              </div>
            </div>
          )}

          {/* Options editor for select / multi_select */}
          {needsOptions && (
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">{t('tasks.options')}</label>
              <div className="space-y-2">
                {options.map(opt => (
                  <div
                    key={opt.id}
                    draggable
                    onDragStart={() => handleDragStart(opt.id)}
                    onDragOver={e => handleDragOver(e, opt.id)}
                    className="flex items-center gap-2 cursor-default"
                  >
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground/70 cursor-grab flex-shrink-0" />
                    {/* Color picker */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-5 h-5 rounded-full border-2 border-border cursor-pointer"
                        style={{ backgroundColor: opt.color }}
                        onClick={() => {
                          const idx = OPTION_COLORS.indexOf(opt.color);
                          updateOption(opt.id, 'color', OPTION_COLORS[(idx + 1) % OPTION_COLORS.length]);
                        }}
                        title={t('ui_views.add_field.change_color')}
                      />
                    </div>
                    <Input
                      value={opt.label}
                      onChange={e => updateOption(opt.id, 'label', e.target.value)}
                      className="bg-accent/60 border-border text-foreground text-sm h-8 flex-1"
                    />
                    <button
                      onClick={() => removeOption(opt.id)}
                      className="text-muted-foreground/70 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addOption}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-amber-700 dark:hover:text-amber-400 transition-colors mt-1"
                >
                  <Plus className="w-3 h-3" />
                  {t('tasks.add_option')}
                </button>
              </div>
            </div>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter className="border-border bg-popover">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground">
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={(!isEditMode && !selectedType) || !fieldName.trim() || loading}
            className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50"
          >
            {loading
              ? (isEditMode ? t('tasks.saving_changes') : t('tasks.creating'))
              : (isEditMode ? t('tasks.save_changes') : t('tasks.create_field'))}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};

export default AddFieldModal;
